<template>
    <div class="modal pca-modal" tabindex="-1" role="dialog" :id="modalId" :aria-labelledby="`${modalId}-title`"
        @keydown.esc.prevent="close">
        <div class="modal-dialog modal-dialog-centered pca-dialog pga-dialog" role="document">
            <div class="modal-content pca" v-if="ctx" :style="glowStyle">
                <header class="pca-head">
                    <button type="button" class="pca-close" :disabled="saving" @click="close">
                        <fa icon="times" />
                        <span class="sr-only">Fechar</span>
                    </button>

                    <div class="pca-bridge" :class="{'is-linked': !!chosen}" aria-hidden="true">
                        <BrandTileCmp :size="40" :image="muvenMarkUrl" background="var(--brand-fill)" color="var(--text-on-brand)" />
                        <span class="pca-bridge-wire">
                            <span class="pca-bridge-node"><fa icon="link" /></span>
                        </span>
                        <BrandTileCmp :size="40" :image="channelMarkUrl" :icon="isCustom ? 'store-alt' : null"
                            :text="brand.monogram" :background="brand.color" :color="brand.textColor" />
                    </div>

                    <h2 class="pca-title" :id="`${modalId}-title`">
                        {{currentParentId ? 'Trocar associação da grade' : 'Associar grade'}} com {{channelLabel}}
                    </h2>
                    <!-- Uma frase do que fazer agora; o andamento aparece no próprio corpo do modal -->
                    <p class="pca-subtitle">
                        <template v-if="step === 'choose'">Primeiro o produto pai {{inChannel}}; depois, o par de cada variação.</template>
                        <template v-else>Confira com qual variação do canal cada variação do Muven fica associada.</template>
                    </p>
                </header>

                <div class="pca-body">
                    <aside class="pca-muven">
                        <p class="pca-eyebrow">No Muven</p>

                        <div class="pca-muven-product">
                            <span class="pca-muven-thumb" :class="{'is-empty': !ctx.product.imageUrl}">
                                <img v-if="ctx.product.imageUrl" :src="ctx.product.imageUrl" alt="" />
                                <fa v-else icon="box" />
                            </span>
                            <p class="pca-muven-name">{{ctx.product.name}}</p>
                        </div>

                        <dl class="pca-muven-facts">
                            <div v-if="ctx.product.axes">
                                <dt>Grade</dt>
                                <dd>{{ctx.product.axes}}</dd>
                            </div>
                            <div>
                                <dt>Variações</dt>
                                <dd>{{ctx.variations.length}}</dd>
                            </div>
                            <div v-if="ctx.product.codeSource">
                                <dt>Cód. retaguarda</dt>
                                <dd><code>{{ctx.product.codeSource}}</code></dd>
                            </div>
                            <!-- Produto em mais de uma loja: a associação vale só para a loja de onde o modal foi aberto -->
                            <div v-if="ctx.storeName" class="pga-fact-store">
                                <dt>Loja</dt>
                                <dd :title="ctx.storeName">{{ctx.storeName}}</dd>
                            </div>
                        </dl>

                        <div class="pca-today" :class="`is-${currentTone}`">
                            <p class="pca-eyebrow">Pai associado hoje</p>
                            <template v-if="currentParentId">
                                <code class="pca-today-id">{{currentParentId}}</code>
                                <p class="pca-today-state" v-if="currentStateText">
                                    <fa :icon="currentTone === 'ok' ? 'check-circle' : 'exclamation-triangle'" />
                                    {{currentStateText}}
                                </p>
                            </template>
                            <p v-else class="pca-today-empty">Sem associação com este canal.</p>
                        </div>
                    </aside>

                    <!-- Passo 1: escolher o produto pai no canal -->
                    <section class="pca-channel" v-if="step === 'choose'">
                        <div class="pca-tabs" role="tablist" aria-label="Como encontrar o produto no canal"
                            v-if="searchProfile.supportsGetById">
                            <button type="button" role="tab" class="pca-tab" :class="{'is-active': tab === 'search'}"
                                :aria-selected="String(tab === 'search')" @click="switchTab('search')">
                                <fa icon="search" /> Buscar no canal
                            </button>
                            <button type="button" role="tab" class="pca-tab" :class="{'is-active': tab === 'id'}"
                                :aria-selected="String(tab === 'id')" @click="switchTab('id')">
                                <fa icon="barcode" /> Já tenho o ID
                            </button>
                        </div>

                        <form v-if="tab === 'search'" class="pca-search" @submit.prevent="searchFromInput">
                            <input ref="searchInput" v-model.trim="query" type="search" class="pca-input"
                                :placeholder="searchHint" :aria-label="`Buscar ${inChannel}: ${searchHint}`"
                                autocomplete="off" spellcheck="false" />
                            <button type="submit" class="pca-btn" :disabled="!query || searching">
                                <Loading v-if="searching" />
                                <template v-else>Buscar</template>
                            </button>
                        </form>

                        <form v-else class="pca-search" @submit.prevent="lookupId">
                            <input ref="idInput" v-model.trim="idQuery" type="text" class="pca-input is-mono"
                                placeholder="ID do produto pai no canal" aria-label="ID do produto pai no canal"
                                autocomplete="off" spellcheck="false" />
                            <button type="submit" class="pca-btn" :disabled="!idQuery || searching">
                                <Loading v-if="searching" />
                                <template v-else>Conferir</template>
                            </button>
                        </form>

                        <p class="pca-search-note" aria-live="polite">
                            <template v-if="tab === 'search'">
                                <template v-if="searchedLabel">
                                    Buscamos por <strong>{{searchedLabel}}</strong>
                                    <template v-if="searchedTerm">: “{{searchedTerm}}”</template>.
                                    <span v-if="fallbackNote" class="pca-fallback">{{fallbackNote}}</span>
                                </template>
                                <template v-else>O canal {{channelLabel}} aceita: {{searchHint}}.</template>
                            </template>
                            <template v-else>Cole o ID do produto pai, como aparece {{inChannel}}.</template>
                        </p>

                        <div class="pca-results" aria-live="polite" :aria-busy="String(searching)">
                            <div v-if="searching" class="pca-skeletons" aria-hidden="true">
                                <span v-for="n in 3" :key="n" class="pca-skeleton"></span>
                            </div>

                            <div v-else-if="resultError" class="pca-empty is-error">
                                <fa icon="info-circle" class="pca-empty-icon" />
                                <p class="pca-empty-title">Não deu para consultar o canal agora</p>
                                <p class="pca-empty-text">{{resultError}}</p>
                                <button type="button" class="pca-link-btn" @click="retry">
                                    <fa icon="sync-alt" /> Tentar de novo
                                </button>
                            </div>

                            <div v-else-if="hasSearched && !results.length" class="pca-empty">
                                <fa icon="search" class="pca-empty-icon" />
                                <p class="pca-empty-title">
                                    {{tab === 'id' ? 'Este ID não existe neste canal' : 'Nenhum produto encontrado'}}
                                </p>
                                <p class="pca-empty-text" v-if="tab === 'search'">
                                    <template v-if="emptyNote">{{emptyNote}} </template>
                                    Tente outro termo. O canal {{channelLabel}} aceita: {{searchHint}}.
                                </p>
                                <p class="pca-empty-text" v-else>Confira se o ID foi copiado inteiro.</p>
                            </div>

                            <div v-else-if="!hasSearched && tab === 'search' && searchProfile.manualGuide" class="pca-empty is-guide">
                                <fa icon="info-circle" class="pca-empty-icon" />
                                <p class="pca-empty-title">Como achar o produto {{inChannel}}</p>
                                <p class="pca-empty-text">{{searchProfile.manualGuide}}</p>
                            </div>

                            <div v-else-if="!hasSearched" class="pca-empty is-idle">
                                <p class="pca-empty-text">
                                    {{tab === 'search' ? 'Digite um termo e busque para ver os produtos do canal.' : 'Informe o ID e confira.'}}
                                </p>
                            </div>

                            <div v-else class="pca-options" role="radiogroup" aria-label="Produtos do canal">
                                <label v-for="item in results" :key="item.key" class="pca-option pga-product"
                                    :class="{'is-selected': chosen === item.product, 'is-disabled': !item.hasVariations, 'is-current': item.isCurrent}">
                                    <input type="radio" class="pca-option-radio" :name="`pga-product-${_uid}`"
                                        :disabled="!item.hasVariations || saving" :checked="chosen === item.product"
                                        @change="choose(item.product)" />
                                    <span class="pca-option-mark" aria-hidden="true"></span>

                                    <span class="pca-option-main">
                                        <span class="pga-product-name">{{item.product.name || 'Produto sem nome'}}</span>
                                        <span class="pca-option-meta">
                                            <span>{{parentLabel}} <code>{{item.product.idExternalChannel}}</code></span>
                                            <span v-if="item.hasVariations">{{item.product.variations.length}} variações</span>
                                            <span v-if="item.product.isActive === false">Inativo no canal</span>
                                        </span>
                                        <span class="pga-preview" v-if="item.hasVariations">
                                            <span v-for="value in item.preview" :key="value" class="pca-attr">{{value}}</span>
                                            <span v-if="item.more" class="pga-preview-more">+{{item.more}}</span>
                                        </span>
                                        <span class="pga-disabled-reason" v-else>
                                            Sem variações no canal: uma grade precisa ser associada a um produto com variações.
                                        </span>
                                    </span>

                                    <span class="pca-option-badges">
                                        <span v-if="item.isCurrent" class="pca-tag is-brand">Associado hoje</span>
                                        <span v-if="item.codeMatches" class="pca-tag is-ok"><fa icon="check-circle" /> Códigos batem</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </section>

                    <!-- Passo 2: casar as variações -->
                    <section class="pca-channel pga-match" v-else>
                        <div class="pga-chosen">
                            <div>
                                <p class="pca-eyebrow">Produto {{inChannel}}</p>
                                <p class="pga-chosen-name">{{chosen.name}}</p>
                                <p class="pca-option-meta">
                                    <span>{{parentLabel}} <code>{{chosen.idExternalChannel}}</code></span>
                                    <span>{{chosen.variations.length}} variações</span>
                                </p>
                            </div>
                            <button type="button" class="pca-link-btn" :disabled="saving" @click="backToChoose">
                                <fa icon="edit" /> Escolher outro produto
                            </button>
                        </div>

                        <div class="pga-section-head">
                            <h3 class="pga-section-title">Par de cada variação</h3>
                            <p class="pca-search-note">
                                Sugerimos o par pelo código e pelos atributos. Confira e ajuste se precisar.
                            </p>
                        </div>

                        <div class="pca-results pga-rows">
                            <div v-for="(row, index) in matchRows" :key="row.variation.idMuven" class="pga-row"
                                :ref="`row-${row.variation.idMuven}`"
                                :class="[`is-${row.tone}`, {'is-focused': String(row.variation.idMuven) === String(ctx.focusVariationId)}]">
                                <div class="pga-row-muven">
                                    <span class="pga-row-values">
                                        <span v-for="value in row.variation.values" :key="value" class="pca-attr">{{value}}</span>
                                    </span>
                                    <span class="pca-option-meta">
                                        <span v-if="row.variation.skuCode">SKU <code>{{row.variation.skuCode}}</code></span>
                                        <span v-if="row.variation.currentId">hoje <code>{{row.variation.currentId}}</code></span>
                                    </span>
                                </div>

                                <fa icon="link" class="pga-row-link" aria-hidden="true" />

                                <div class="pga-row-channel">
                                    <select class="pga-select" :aria-label="`Variação ${inChannel} para ${row.variation.values.join(' ')}`"
                                        :value="row.pair.idChannelVariation || ''" :disabled="saving"
                                        @change="setPair(index, $event.target.value)">
                                        <option value="">Sem associação</option>
                                        <option v-for="option in channelOptions" :key="option.id" :value="option.id" :title="`ID ${option.id}`">{{option.label}}</option>
                                    </select>
                                    <span class="pga-row-reason">
                                        <span class="pca-tag" :class="`is-${row.reasonTone}`">{{row.reasonLabel}}</span>
                                        <span v-if="row.change" class="pga-change">{{row.change}}</span>
                                    </span>
                                    <span v-if="row.duplicated" class="pga-row-error">Esta variação do canal já foi escolhida em outra linha.</span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <footer class="pca-foot" :class="`is-${footTone}`" aria-live="polite">
                    <template v-if="footStep === 'remove'">
                        <div class="pca-foot-text">
                            <p class="pca-foot-title">Remover a associação da grade com {{channelLabel}}?</p>
                            <p class="pca-foot-sub">
                                O produto pai e as {{ctx.variations.length}} variações ficam sem vínculo com o canal.
                                Pedidos com elas vão dar erro até uma nova associação.
                            </p>
                        </div>
                        <div class="pca-foot-actions">
                            <button type="button" class="pca-btn is-ghost" :disabled="saving" @click="footStep = 'idle'">Voltar</button>
                            <button type="button" class="pca-btn is-danger" :disabled="saving" @click="remove">
                                <Loading v-if="saving" />
                                <template v-else>Remover associação</template>
                            </button>
                        </div>
                    </template>

                    <template v-else-if="footStep === 'confirm'">
                        <div class="pca-foot-text">
                            <p class="pca-foot-title">Confirmar {{changeList.length}} {{changeList.length === 1 ? 'mudança' : 'mudanças'}}?</p>
                            <ul class="pga-changes">
                                <li v-for="change in changeList" :key="change.key">
                                    <strong>{{change.label}}</strong>
                                    <code v-if="change.from">{{change.from}}</code><span v-else class="pga-none">sem associação</span>
                                    <span class="pga-arrow">→</span>
                                    <code v-if="change.to">{{change.to}}</code><span v-else class="pga-none">sem associação</span>
                                </li>
                            </ul>
                        </div>
                        <div class="pca-foot-actions">
                            <button type="button" class="pca-btn is-ghost" :disabled="saving" @click="footStep = 'idle'">Voltar</button>
                            <button type="button" class="pca-btn is-primary" ref="confirmButton" :disabled="saving" @click="save">
                                <Loading v-if="saving" />
                                <template v-else>Confirmar</template>
                            </button>
                        </div>
                    </template>

                    <template v-else>
                        <div class="pca-foot-text">
                            <template v-if="step === 'choose'">
                                <p class="pca-foot-sub">Escolha o produto {{inChannel}} que corresponde a esta grade.</p>
                            </template>
                            <template v-else>
                                <p class="pca-foot-label">Resumo</p>
                                <p class="pga-summary">
                                    <span><strong>{{pairedCount}}</strong> de {{ctx.variations.length}} variações com par</span>
                                    <span v-if="duplicates.size" class="pga-summary-bad">{{duplicates.size}} repetida(s)</span>
                                    <span v-else-if="changeList.length">{{changeList.length}} {{changeList.length === 1 ? 'mudança' : 'mudanças'}}</span>
                                    <span v-else>nada mudou</span>
                                </p>
                            </template>

                            <button v-if="currentParentId" type="button" class="pca-remove-link" :disabled="saving" @click="footStep = 'remove'">
                                <fa icon="trash-alt" /> Remover associação da grade
                            </button>
                        </div>
                        <div class="pca-foot-actions">
                            <button type="button" class="pca-btn is-ghost" :disabled="saving" @click="close">Cancelar</button>
                            <button type="button" class="pca-btn is-primary" :disabled="!canSave" @click="askConfirm">
                                {{currentParentId ? 'Trocar associação' : 'Associar grade'}}
                            </button>
                        </div>
                    </template>

                    <p v-if="saveError" class="pca-foot-error">{{saveError}}</p>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { ChannelEnum } from '@c2b/muven-commons'
import productService from '@/product/services/ProductService'
import modalService from '@/utils/services/ModalService'
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'
import {
    buildGridAssociationPayload,
    buildGridRemovalPayload,
    findDuplicatePairs,
    getChannelSearchHint,
    getChannelSearchProfile,
    getChannelVariationValues,
    getSuggestedSearches,
    isNotFoundError,
    suggestGridPairs,
} from '@/channel/utils/ChannelProductAssociationHelper'

const PREVIEW_LIMIT = 6

const REASONS = {
    code: {label: 'Mesmo código', tone: 'ok'},
    attributes: {label: 'Mesmos atributos', tone: 'ok'},
    current: {label: 'Associado hoje', tone: 'brand'},
    manual: {label: 'Escolhido por você', tone: 'brand'},
    none: {label: 'Sem par', tone: 'muted'},
}

/**
 * Associação guiada de uma grade: o pai do Muven com o pai do canal e cada
 * variação com uma variação dele (mesma regra do linkProducts da tela de
 * associação). Sugere os pares por código e atributos; o usuário confere.
 *
 *   const result = await this.$refs.gridModal.open({...})
 *   // {entries: [{idMuven, idExternalChannel}]} gravou | undefined cancelou
 */
export default {
    name: 'ProductGridAssociationModalCmp',
    components: {
        BrandTileCmp,
    },
    data() {
        return {
            ctx: null,
            step: 'choose',
            tab: 'search',
            query: '',
            idQuery: '',
            searching: false,
            hasSearched: false,
            resultError: '',
            emptyNote: '',
            lastAction: null,
            searchedLabel: '',
            searchedTerm: '',
            fallbackNote: '',
            products: [],
            chosen: null,
            pairs: [],
            footStep: 'idle',
            saving: false,
            saveError: '',
            resolver: null,
            requestSeq: 0,
        }
    },
    computed: {
        modalId() {
            return `product-grid-association-${this._uid}`
        },
        enumChannel() {
            return this.ctx ? this.ctx.storeChannel.channel.enumChannel : null
        },
        channelLabel() {
            return this.ctx ? this.ctx.storeChannel.channel.name : ''
        },
        inChannel() {
            return `no canal ${this.channelLabel}`
        },
        channelId() {
            return this.ctx.storeChannel.idChannel || this.ctx.storeChannel.channel.id
        },
        brand() {
            return getChannelBrand(this.enumChannel)
        },
        channelMarkUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        muvenMarkUrl() {
            return require('@/channel/images/marks/muven-cart.png')
        },
        isCustom() {
            return this.enumChannel === ChannelEnum.Custom
        },
        glowStyle() {
            const glow = getChannelGlow(this.brand)

            return glow ? {'--pca-glow': glow} : {}
        },
        searchProfile() {
            return getChannelSearchProfile(this.enumChannel)
        },
        searchHint() {
            return getChannelSearchHint(this.enumChannel)
        },
        parentLabel() {
            return this.enumChannel === ChannelEnum.Shopee ? 'Anúncio' : 'Produto'
        },
        currentParentId() {
            return (this.ctx && this.ctx.storeChannel.idExternalChannel) || ''
        },
        currentTone() {
            const state = this.ctx && this.ctx.current ? this.ctx.current.state : ''

            return {gridOk: 'ok', notFound: 'bad', gridSimple: 'warn', gridVariation: 'bad'}[state] || 'neutral'
        },
        currentStateText() {
            const state = this.ctx && this.ctx.current ? this.ctx.current.state : ''

            return {
                gridOk: 'Confere no canal',
                notFound: 'Não encontrado no canal',
                gridSimple: 'No canal é um produto sem variações',
                gridVariation: 'É o ID de uma variação, não do pai',
            }[state] || ''
        },
        // Códigos da grade no Muven: do pai e das variações
        muvenCodes() {
            const codes = [this.ctx.product.skuCode, this.ctx.product.codeSource]

            this.ctx.variations.forEach((v) => codes.push(v.skuCode, v.codeSource))

            return codes.filter((c) => !!c).map((c) => String(c).trim().toLowerCase())
        },
        results() {
            return this.products.map((product, index) => {
                const variations = product.variations || []
                const values = []

                variations.forEach((v) => getChannelVariationValues(v).forEach((value) => {
                    if (value && !values.includes(value)) {
                        values.push(value)
                    }
                }))

                return {
                    key: `${product.idExternalChannel}-${index}`,
                    product,
                    hasVariations: variations.length > 0,
                    isCurrent: !!this.currentParentId && String(product.idExternalChannel) === String(this.currentParentId),
                    codeMatches: variations.some((v) => this.muvenCodes.includes(String(v.skuCode || '').trim().toLowerCase())),
                    preview: values.slice(0, PREVIEW_LIMIT),
                    more: Math.max(0, values.length - PREVIEW_LIMIT),
                }
            })
        },
        channelOptions() {
            return ((this.chosen && this.chosen.variations) || []).map((v) => {
                const id = String(v.idExternalChannel)
                const values = getChannelVariationValues(v)
                const parts = [values.length ? values.join(' · ') : 'Variação']

                if (v.skuCode) {
                    parts.push(`SKU ${v.skuCode}`)
                }

                // O ID só ajuda quando o SKU não o identifica (sem SKU, ou ID sem o SKU dentro)
                const idIsRedundant = !!v.skuCode && id.includes(String(v.skuCode))

                return {id, label: idIsRedundant ? parts.join(' · ') : `${parts.join(' · ')} (ID ${id})`}
            })
        },
        duplicates() {
            return findDuplicatePairs(this.pairs)
        },
        matchRows() {
            return this.ctx.variations.map((variation, index) => {
                const pair = this.pairs[index] || {idChannelVariation: null, reason: 'none'}
                const reason = REASONS[pair.reason] || REASONS.none
                const current = variation.currentId || ''
                const next = pair.idChannelVariation || ''
                let change = ''

                if (current !== next) {
                    change = !current ? 'nova' : (!next ? `remove ${current}` : `de ${current} para ${next}`)
                }

                const duplicated = !!next && this.duplicates.has(next)

                return {
                    variation,
                    pair,
                    reasonLabel: reason.label,
                    reasonTone: reason.tone,
                    change,
                    duplicated,
                    tone: duplicated ? 'bad' : (next ? 'ok' : 'empty'),
                }
            })
        },
        pairedCount() {
            return this.pairs.filter((p) => !!p.idChannelVariation).length
        },
        payload() {
            if (!this.chosen) {
                return []
            }

            return buildGridAssociationPayload(this.ctx.idMuven, String(this.chosen.idExternalChannel), this.pairs, this.ctx.variations)
        },
        // Mudanças legíveis para a confirmação (o pai só entra se mudou)
        changeList() {
            if (!this.chosen) {
                return []
            }

            const list = []
            const newParent = String(this.chosen.idExternalChannel)

            if (newParent !== this.currentParentId) {
                list.push({key: 'parent', label: 'Produto pai', from: this.currentParentId, to: newParent})
            }

            this.matchRows.forEach((row) => {
                if (row.change) {
                    list.push({
                        key: `v-${row.variation.idMuven}`,
                        label: row.variation.values.join(' · ') || row.variation.skuCode,
                        from: row.variation.currentId || '',
                        to: row.pair.idChannelVariation || '',
                    })
                }
            })

            return list
        },
        canSave() {
            return this.step === 'match' && !!this.chosen && !this.duplicates.size && this.changeList.length > 0 && !this.saving
        },
        footTone() {
            if (this.footStep === 'remove') {
                return 'danger'
            }

            if (this.footStep === 'confirm') {
                return 'confirm'
            }

            return this.canSave ? 'ready' : 'idle'
        },
    },
    methods: {
        /**
         * @param {{storeChannel, idStore, storeName, idMuven, product: {name, skuCode, codeSource, imageUrl, axes},
         *   variations: Array<{idMuven, values, skuCode, codeSource, currentId}>, current, focusVariationId}} ctx
         */
        open(ctx) {
            Object.assign(this.$data, this.$options.data.call(this))
            this.ctx = ctx

            return new Promise((resolve) => {
                this.resolver = resolve
                modalService.show(`#${this.modalId}`)
                this.$nextTick(() => this.start())
            })
        },
        async close() {
            if (this.saving) {
                return
            }

            await this.finish(undefined)
        },
        async finish(result) {
            await modalService.hideAndWait(`#${this.modalId}`)

            if (this.resolver) {
                this.resolver(result)
                this.resolver = null
            }

            this.ctx = null
        },
        // Com o pai já associado, abre direto no casamento das variações; senão procura o produto
        async start() {
            if (this.currentParentId && this.searchProfile.supportsGetById) {
                this.tab = 'id'
                this.idQuery = this.currentParentId
                await this.lookupId()

                const current = this.results.find((r) => r.isCurrent && r.hasVariations)

                if (current) {
                    this.choose(current.product)
                    return
                }

                this.tab = 'search'
                this.idQuery = ''
            }

            await this.autoSearch()
        },
        // Busca automática: os termos do pai e, para canais de código exato, também os da 1ª variação
        // (os agentes acham a variação e devolvem o produto pai)
        suggestions() {
            const fromParent = getSuggestedSearches(this.enumChannel, this.ctx.product)
            const first = this.ctx.variations[0]
            const fromVariation = first
                ? getSuggestedSearches(this.enumChannel, {skuCode: first.skuCode, codeSource: first.codeSource})
                    .map((s) => ({...s, label: `${s.label}, da 1ª variação`}))
                : []
            const seen = new Set()

            return [...fromParent, ...fromVariation].filter((s) => {
                const key = s.term.toLowerCase()

                if (seen.has(key)) {
                    return false
                }

                seen.add(key)
                return true
            })
        },
        async autoSearch() {
            const suggestions = this.suggestions()

            for (let i = 0; i < suggestions.length; i++) {
                this.query = suggestions[i].term

                const found = await this.runSearch(suggestions[i].term, suggestions[i].label)

                if (found || this.resultError || !this.ctx) {
                    if (i > 0 && found) {
                        this.fallbackNote = `Nada por ${suggestions[0].label.toLowerCase()}, então tentamos este.`
                    }
                    break
                }
            }

            this.focusInput()
        },
        focusInput() {
            setTimeout(() => {
                const input = this.tab === 'search' ? this.$refs.searchInput : this.$refs.idInput

                if (input) {
                    input.focus()
                }
            }, 250)
        },
        switchTab(tab) {
            if (this.tab === tab) {
                return
            }

            this.tab = tab
            this.products = []
            this.hasSearched = false
            this.resultError = ''
            this.focusInput()
        },
        searchFromInput() {
            this.fallbackNote = ''
            this.runSearch(this.query, '')
        },
        async runSearch(term, label) {
            const seq = ++this.requestSeq
            this.lastAction = () => this.runSearch(term, label)
            this.startLoading()
            this.searchedLabel = label || 'o termo digitado'
            this.searchedTerm = term

            try {
                const products = await productService.getAssociableProductInChannelByAnyInfo(this.ctx.idStore, this.channelId, term)

                if (seq !== this.requestSeq) {
                    return false
                }

                this.products = this.onlyExact(products || [], term)
            } catch (err) {
                if (seq !== this.requestSeq) {
                    return false
                }

                if (this.searchProfile.searchErrorMeansEmpty) {
                    this.emptyNote = 'O canal não encontrou o código ou não respondeu.'
                } else if (!isNotFoundError(err)) {
                    this.resultError = (err && err.message) || 'O canal não respondeu.'
                }
            }

            this.stopLoading()

            return this.products.length > 0
        },
        // Canal que devolve qualquer produto quando não acha: fica só o que tem o código buscado
        onlyExact(products, term) {
            if (!this.searchProfile.exactCodeOnly) {
                return products
            }

            const code = String(term || '').trim().toLowerCase()
            const same = (c) => String(c || '').trim().toLowerCase() === code

            return products.filter((p) => same(p.skuCode) || (p.variations || []).some((v) => same(v.skuCode)))
        },
        async lookupId() {
            const id = this.idQuery
            const seq = ++this.requestSeq
            this.lastAction = () => this.lookupId()
            this.startLoading()

            try {
                const product = await productService.getAssociableProductInChannelByIdExternalChannel(this.ctx.idStore, this.channelId, id)

                if (seq !== this.requestSeq) {
                    return
                }

                this.products = product && (product.name || product.idExternalChannel) ? [product] : []
            } catch (err) {
                if (seq !== this.requestSeq) {
                    return
                }

                if (!isNotFoundError(err)) {
                    this.resultError = (err && err.message) || 'O canal não respondeu.'
                }
            }

            this.stopLoading()
        },
        startLoading() {
            this.searching = true
            this.hasSearched = false
            this.resultError = ''
            this.emptyNote = ''
            this.products = []
            this.saveError = ''
        },
        stopLoading() {
            this.searching = false
            this.hasSearched = true
        },
        retry() {
            if (this.lastAction) {
                this.lastAction()
            }
        },
        choose(product) {
            this.chosen = product
            this.pairs = suggestGridPairs(this.ctx.variations, product)
            this.step = 'match'
            this.footStep = 'idle'
            this.saveError = ''
            this.$nextTick(() => this.focusVariationRow())
        },
        // Aberto a partir de uma variação: leva até a linha dela
        focusVariationRow() {
            if (!this.ctx || !this.ctx.focusVariationId) {
                return
            }

            const ref = this.$refs[`row-${this.ctx.focusVariationId}`]
            const el = Array.isArray(ref) ? ref[0] : ref

            if (el) {
                el.scrollIntoView({block: 'center'})
                const select = el.querySelector('select')

                if (select) {
                    setTimeout(() => select.focus(), 250)
                }
            }
        },
        backToChoose() {
            this.step = 'choose'
            this.footStep = 'idle'

            if (!this.products.length) {
                this.autoSearch()
            }
        },
        setPair(index, value) {
            this.$set(this.pairs, index, {...this.pairs[index], idChannelVariation: value || null, reason: value ? 'manual' : 'none'})
            this.footStep = 'idle'
        },
        askConfirm() {
            if (!this.canSave) {
                return
            }

            this.footStep = 'confirm'
            this.$nextTick(() => this.$refs.confirmButton && this.$refs.confirmButton.focus())
        },
        async persist(entries) {
            this.saving = true
            this.saveError = ''

            try {
                await productService.updateProductAssociation(this.ctx.idStore, this.channelId, entries)

                return true
            } catch (err) {
                this.saveError = (err && err.message) || 'Não foi possível salvar a associação.'
                this.footStep = 'idle'

                return false
            } finally {
                this.saving = false
            }
        },
        async save() {
            const entries = this.payload

            if (await this.persist(entries)) {
                this.$floatingAlert.success({message: `Grade associada ao produto <b>${this.chosen.idExternalChannel}</b> no canal ${this.channelLabel}.`})
                await this.finish({entries})
            }
        },
        async remove() {
            const entries = buildGridRemovalPayload(this.ctx.idMuven, this.ctx.variations)

            if (await this.persist(entries)) {
                this.$floatingAlert.success({message: `Associação da grade com ${this.channelLabel} removida.`})
                await this.finish({entries})
            }
        },
    },
    mounted() {
        // No body: um ancestral com transform prenderia o position: fixed do modal
        document.body.appendChild(this.$el)
    },
    beforeDestroy() {
        modalService.hide(`#${this.modalId}`)
    },
    destroyed() {
        if (this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el)
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/product/styles/ChannelAssociationModal.scss';

.pga-dialog {
    max-width: 1000px;
}

// Loja ocupa a linha inteira dos fatos: nomes de loja são longos
.pga-fact-store {
    flex-basis: 100%;
    min-width: 0;

    dd {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.pga-section-head {
    margin: 16px 0 10px;

    .pca-search-note {
        margin: 2px 0 0;
    }
}

.pga-section-title {
    margin: 0;
    color: var(--text-strong);
    font-size: 14px;
    font-weight: 700;
}

.pga-product {
    align-items: flex-start;

    .pca-option-mark {
        margin-top: 2px;
    }

    &.is-disabled {
        cursor: not-allowed;
        opacity: .6;

        &:hover {
            border-color: var(--border-subtle);
        }
    }
}

.pga-product-name {
    color: var(--text-strong);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.35;
}

.pga-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 2px;

    .pca-attr {
        font-size: 12px;
        font-weight: 600;
    }
}

.pga-preview-more {
    align-self: center;
    color: var(--text-faint);
    font-size: 12px;
    font-weight: 700;
}

.pga-disabled-reason {
    color: var(--text-muted);
    font-size: 12px;
}

.pga-chosen {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px 16px;
    margin-bottom: 8px;
    padding: 12px 14px;
    border: 1px solid var(--tint-brand-border);
    border-radius: 12px;
    background: var(--tint-brand-soft);

    .pca-eyebrow {
        margin-bottom: 2px;
    }

    .pca-link-btn {
        margin-top: 0;
    }
}

.pga-chosen-name {
    margin: 0;
    color: var(--text-strong);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
}

.pga-rows {
    display: grid;
    align-content: start;
    gap: 8px;
}

// Uma linha por variação do Muven: atributos à esquerda, a variação do canal à direita
.pga-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20px minmax(0, 1.25fr);
    align-items: center;
    gap: 10px 12px;
    padding: 10px 12px;
    border: 1px solid var(--border-subtle);
    border-left: 3px solid var(--border-strong);
    border-radius: 10px;
    background: var(--surface-1);
    animation: pca-rise .3s $ease-out both;
    transition: border-color .2s, box-shadow .2s;

    &.is-ok { border-left-color: var(--success-solid); }
    &.is-bad { border-left-color: var(--danger-solid); }

    &.is-focused {
        box-shadow: 0 0 0 2px var(--brand-soft);
    }

    @media (max-width: 767px) {
        grid-template-columns: minmax(0, 1fr);

        .pga-row-link {
            display: none;
        }
    }
}

.pga-row-muven {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.pga-row-values {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.pga-row-link {
    color: var(--text-faint);
    font-size: 12px;

    .is-ok & {
        color: var(--success-solid);
    }
}

.pga-row-channel {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
}

.pga-select {
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border: 1px solid var(--border-strong);
    border-radius: 9px;
    color: var(--text-strong);
    background: var(--surface-1);
    font-size: 13px;

    &:focus {
        border-color: var(--brand-soft);
        box-shadow: 0 0 0 3px var(--focus-ring);
        outline: none;
    }

    .is-bad & {
        border-color: var(--danger-solid);
    }
}

.pga-row-reason {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.pga-change {
    color: var(--text-muted);
    font-family: $mono;
    font-size: 11px;
}

.pga-row-error {
    color: var(--danger-fg);
    font-size: 12px;
    font-weight: 600;
}

.pga-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 0;
    color: var(--text-muted);
    font-size: 14px;

    strong {
        color: var(--text-strong);
    }
}

.pga-summary-bad {
    color: var(--danger-fg);
    font-weight: 700;
}

.pga-changes {
    max-height: 140px;
    margin: 6px 0 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
    font-size: 13px;

    li {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        padding: 2px 0;
        color: var(--text-muted);
    }

    strong {
        min-width: 90px;
        color: var(--text-strong);
    }
}

.pga-arrow {
    color: var(--text-faint);
}

.pga-none {
    color: var(--text-faint);
    font-style: italic;
}
</style>
